
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Sparkles, Star, Award, ShoppingCart, Timer } from "lucide-react";
import { Product } from "@/types/common";
import { hasDiscount } from "@/utils/productTypeUtils";
import PriceDisplay from "@/components/pricing/PriceDisplay";

interface PremiumProductInfoProps {
  product: Product;
  onAddToCart: (e: React.MouseEvent) => void;
}

const PremiumProductInfo = ({ product, onAddToCart }: PremiumProductInfoProps) => {
  const productHasDiscount = hasDiscount(product);
  const isLowStock = product.stock_quantity !== null && product.stock_quantity <= 5;
  const isOutOfStock = product.stock_quantity === 0;

  return (
    <div className="p-6 space-y-4">
      {/* Category with Premium Styling */}
      {product.categories?.name && (
        <div className="flex items-center gap-2">
          <Sparkles className="h-3 w-3 text-primary" />
          <span className="text-xs font-medium text-primary uppercase tracking-wider">
            {product.categories.name}
          </span>
        </div>
      )}

      {/* Product Name */}
      <h3 className="text-lg font-semibold text-foreground leading-tight line-clamp-2 min-h-[3.5rem]">
        {product.name}
      </h3>

      {/* Premium Rating Display */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star 
              key={i} 
              className="h-4 w-4 fill-yellow-400 text-yellow-400" 
            />
          ))}
          <span className="text-sm text-muted-foreground ml-2">(4.9)</span>
        </div>
        
        {product.featured && (
          <div className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-yellow-100 to-yellow-200 rounded-full">
            <Award className="h-3 w-3 text-yellow-600" />
            <span className="text-xs font-medium text-yellow-700">Bestseller</span>
          </div>
        )}
      </div>

      {/* Premium Price Display */}
      <div className="flex items-baseline justify-between">
        <PriceDisplay
          price={product.price}
          compareAtPrice={product.compare_at_price}
          showDiscount={productHasDiscount}
          className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent"
        />
      </div>

      {/* Premium Add to Cart Button */}
      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
        <Button
          onClick={onAddToCart}
          disabled={isOutOfStock}
          className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground border-0 rounded-xl py-3 font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
        >
          {isOutOfStock ? (
            'Notify When Available'
          ) : (
            <>
              <ShoppingCart className="w-4 h-4 mr-2" />
              Add to Cart
            </>
          )}
        </Button>
      </motion.div>

      {/* Stock Status */}
      {isLowStock && !isOutOfStock && (
        <motion.div
          className="flex items-center justify-center gap-2 text-orange-600 text-sm"
          animate={{ opacity: [1, 0.7, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Timer className="h-3 w-3" />
          Only {product.stock_quantity} remaining
        </motion.div>
      )}
    </div>
  );
};

export default PremiumProductInfo;
